import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Users, FileText, CheckCircle, Clock, MapPin, Search, Filter, Activity, AlertTriangle, ShieldAlert, Target, ChevronRight } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import { Button } from '../components/Button';
import { BINDA_LIST, statusColor } from '../data/binda';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { StatusBadge } from '../components/StatusBadge';
import { KPICard } from '../components/KPICard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/Select';
import { ExportDropdown } from '../components/ExportDropdown';
import { FilterBar, FilterField } from '../components/FilterBar';
import { EmptyState } from '../components/EmptyState';
import { useStore, isOfficialProduct } from '../data/store';
import { getBinda, getRegions, getUnits, getPersonnelByBinda, scoreColor } from '../data/kpiIntelligence';
import { UrgencyBadge } from '../components/UrgencyBadge';
export default function RegionalDashboard() {
  const {
    kode
  } = useParams();
  const navigate = useNavigate();
  const regionData = BINDA_LIST.find(b => b.kode === kode) || BINDA_LIST.find(b => b.provinsi === 'DKI Jakarta')!;
  const activeKode = regionData.kode;
  const kpiData = getBinda(activeKode);
  const regions = getRegions(activeKode);
  const units = getUnits(activeKode);
  const personnel = getPersonnelByBinda(activeKode);
  const reportsList = useStore(s => s.reports);
  const alertsList = useStore(s => s.alerts);
  const regionalReports = reportsList.filter(r => r.region === regionData.provinsi || r.provinsi === regionData.provinsi);
  const regionalAlerts = alertsList.filter(a => a.location === regionData.provinsi || a.location.includes(regionData.provinsi));
  const priorityReports = regionalReports.filter(r => r.urgency === 'Kritis' || r.urgency === 'Tinggi' || r.urgency === 'KRITIS' || r.urgency === 'TINGGI').slice(0, 5);
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filteredPersonnel = useMemo(() => {
    return personnel.filter(p => {
      const search = filterValues.search?.toLowerCase() || '';
      const matchesSearch = !search || p.nama.toLowerCase().includes(search) || p.id.toLowerCase().includes(search);
      const matchesUnit = !filterValues.unit || filterValues.unit === 'all' || p.unitId === filterValues.unit;
      const matchesStatus = !filterValues.status || filterValues.status === 'all' || p.status === filterValues.status;
      return matchesSearch && matchesUnit && matchesStatus;
    });
  }, [personnel, filterValues]);
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari ID, nama...'
  }, {
    key: 'provinsi',
    label: 'Provinsi',
    type: 'select',
    options: BINDA_LIST.map(b => ({
      label: b.provinsi,
      value: b.provinsi
    }))
  }, {
    key: 'binda',
    label: 'BINDA',
    type: 'select',
    options: BINDA_LIST.map(b => ({
      label: b.binda,
      value: b.kode
    }))
  }, {
    key: 'kabupaten',
    label: 'Kabupaten/Kota',
    type: 'select',
    options: regions.map(r => ({
      label: r.nama,
      value: r.id
    }))
  }, {
    key: 'kecamatan',
    label: 'Kecamatan',
    type: 'select',
    options: [{
      label: 'Kecamatan Pusat',
      value: 'pusat'
    }, {
      label: 'Kecamatan Utara',
      value: 'utara'
    }, {
      label: 'Kecamatan Selatan',
      value: 'selatan'
    }]
  }, {
    key: 'unit',
    label: 'Unit',
    type: 'select',
    options: units.map(u => ({
      label: u.nama,
      value: u.id
    }))
  }, {
    key: 'status',
    label: 'Status',
    type: 'statusBadge',
    options: [{
      label: 'Aktif',
      value: 'Aktif'
    }, {
      label: 'Perlu Pembinaan',
      value: 'Perlu Pembinaan'
    }, {
      label: 'Tidak Aktif',
      value: 'Tidak Aktif'
    }]
  }, {
    key: 'tanggal',
    label: 'Tanggal',
    type: 'datePreset'
  }];
  const exportColumns = [{
    key: 'id',
    label: 'ID'
  }, {
    key: 'nama',
    label: 'Nama'
  }, {
    key: 'pangkat',
    label: 'Pangkat'
  }, {
    key: 'unitId',
    label: 'ID Unit'
  }, {
    key: 'status',
    label: 'Status'
  }, {
    key: 'score',
    label: 'Skor KPI'
  }, {
    key: 'laporanValid',
    label: 'Laporan Valid'
  }];
  const reportsWithGeotag = regionalReports.filter(r => r.geotag);
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-3010">
      {/* Header & Breadcrumbs */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-end border-b border-border/50 pb-4 gap-4" data-id="element-3011">
        <div data-id="element-3012">
          <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-2" data-id="element-3013">
            <Link to="/" className="hover:text-primary transition-colors" data-id="element-3014">
              Nasional
            </Link>
            <ChevronRight className="w-3 h-3" data-id="element-3015" />
            <span className="text-foreground" data-id="element-3016">{regionData.provinsi}</span>
          </div>
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground uppercase" data-id="element-3017">
            KOMANDO REGIONAL: {regionData.provinsi}
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3018">
            {regionData.binda} // Ringkasan Operasional // Ringkasan Operasional
            // Ringkasan Operasional // Ringkasan Operasional // Ringkasan
            Operasional // Ringkasan Operasional // Ringkasan Operasional //
            Ringkasan Operasional // Ringkasan Operasional // Ringkasan
            Operasional
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-3019">
          <Select value={regionData.kode} onValueChange={value => navigate(`/regional/${value}`)} data-id="element-3020">
            <SelectTrigger size="sm" className="w-[220px] font-mono text-xs" data-id="element-3021">
              <SelectValue placeholder="Pilih Wilayah / BINDA" data-id="element-3022" />
            </SelectTrigger>
            <SelectContent data-id="element-3023">
              {[...BINDA_LIST].sort((a, b) => a.provinsi.localeCompare(b.provinsi)).map(b => <SelectItem key={b.kode} value={b.kode} data-id="element-3024">
                    {b.provinsi}
                  </SelectItem>)}
            </SelectContent>
          </Select>
          <StatusBadge status={regionData.status} className="text-xs px-3 py-1.5" showDot data-id="element-3025" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3026">
        <KPICard title="Total Operatif" value={kpiData?.personelAktif || regionData.personelAktif} icon={Users} colorVar="var(--primary)" trend={{
        value: `${kpiData?.trend || 0}%`,
        label: 'vs Bulan Lalu',
        isPositive: (kpiData?.trend || 0) >= 0
      }} onClick={() => navigate(`/kpi/binda/${activeKode}`)} data-id="element-3027" />
        <KPICard title="Laporan Sektor" value={kpiData?.laporanTotal || regionData.laporanHariIni} icon={FileText} colorVar="var(--status-info)" trend={{
        value: `${kpiData?.laporanValid || 0}`,
        label: 'LAPORAN VALID',
        isPositive: true
      }} onClick={() => navigate(`/reports`)} data-id="element-3028" />
        <KPICard title="Tingkat Validasi" value={`${kpiData?.rasioValid || regionData.kpiWilayah}%`} icon={CheckCircle} colorVar="var(--status-active)" progress={{
        value: kpiData?.rasioValid || regionData.kpiWilayah,
        label: 'TARGET: 85% (NOMINAL)',
        target: '85%'
      }} onClick={() => navigate(`/kpi/binda/${activeKode}`)} data-id="element-3029" />
        <KPICard title="Rata-rata Respon" value={kpiData?.responsRataRata || '14m'} icon={Clock} colorVar="var(--muted-foreground)" trend={{
        value: 'T-MINUS',
        label: 'VERIFIKASI',
        isPositive: true
      }} onClick={() => navigate(`/kpi/binda/${activeKode}`)} data-id="element-3030" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3031">
        {/* Regional Map */}
        <Card className="lg:col-span-2 glass-panel border-border/50 flex flex-col overflow-hidden min-h-[400px]" data-id="element-3032">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-3033">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3034">
              <MapPin className="w-4 h-4 text-primary" data-id="element-3035" /> Peta Sektor Operasi
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 relative bg-[#0a0e17]" data-id="element-3036">
            <MapContainer key={regionData.kode} center={[regionData.lat, regionData.lng]} zoom={9} style={{
            height: '100%',
            width: '100%',
            background: 'transparent'
          }} zoomControl={false} attributionControl={false} data-id="element-3037">
              <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-3038" />

              {/* Render reports with geotags */}
              {reportsWithGeotag.map(report => <CircleMarker key={report.id} center={[report.geotag!.lat, report.geotag!.lng]} radius={8} pathOptions={{
              color: report.urgency === 'Kritis' ? 'var(--destructive)' : 'var(--status-standby)',
              fillColor: report.urgency === 'Kritis' ? 'var(--destructive)' : 'var(--status-standby)',
              fillOpacity: 0.6,
              weight: 2
            }} eventHandlers={{
              click: () => navigate(`/reports/${report.id}`)
            }} data-id="element-3039">
                  <LeafletTooltip className="bg-black/90 border border-border/50 text-foreground font-mono text-xs" data-id="element-3040">
                    <div className="font-bold mb-1" data-id="element-3041">{report.id}</div>
                    <div data-id="element-3042">{report.title}</div>
                    <div className="text-muted-foreground mt-1" data-id="element-3043">
                      {report.category}
                    </div>
                  </LeafletTooltip>
                </CircleMarker>)}

              {/* Center marker for the region itself */}
              <CircleMarker center={[regionData.lat, regionData.lng]} radius={20} pathOptions={{
              color: 'var(--primary)',
              fillColor: 'var(--primary)',
              fillOpacity: 0.1,
              weight: 1
            }} data-id="element-3044" />
            </MapContainer>
            <div className="absolute bottom-3 left-3 z-[400] glass-panel p-2 rounded border border-border/30 pointer-events-none" data-id="element-3045">
              <div className="text-[9px] font-mono text-muted-foreground uppercase" data-id="element-3046">
                Pusat Koordinat: {regionData.lat.toFixed(4)},{' '}
                {regionData.lng.toFixed(4)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Executive & Intelligence Summary */}
        <div className="space-y-6 flex flex-col" data-id="element-3047">
          <Card className="glass-panel border-border/50" data-id="element-3048">
            <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-3049">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3050">
                <Target className="w-4 h-4 text-primary" data-id="element-3051" /> Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-3052">
              <div className="text-sm leading-relaxed text-foreground/90" data-id="element-3053">
                Status operasional di wilayah{' '}
                <strong className="text-primary" data-id="element-3054">{regionData.provinsi}</strong>{' '}
                saat ini berada pada tingkat{' '}
                <StatusBadge status={regionData.status} className="inline-flex ml-1" data-id="element-3055" />
                . Dengan total{' '}
                <strong className="text-primary" data-id="element-3056">
                  {kpiData?.personelAktif || regionData.personelAktif}
                </strong>{' '}
                personel aktif, terdapat{' '}
                <strong className="text-[var(--status-standby)]" data-id="element-3057">
                  {priorityReports.length}
                </strong>{' '}
                laporan prioritas yang memerlukan atensi segera.
              </div>
              <div className="p-3 bg-black/30 border border-border/30 rounded text-xs font-mono text-muted-foreground" data-id="element-3058">
                Kinerja KPI wilayah mencatatkan skor{' '}
                <strong style={{
                color: scoreColor(kpiData?.score || 0)
              }} data-id="element-3059">
                  {kpiData?.score || 0}
                </strong>{' '}
                (Grade {kpiData?.grade || '-'}), menunjukkan tren{' '}
                {kpiData && kpiData.trend >= 0 ? 'positif' : 'negatif'} sebesar{' '}
                {Math.abs(kpiData?.trend || 0)}%.
              </div>
            </CardContent>
          </Card>

          <Card className="glass-panel border-border/50 flex-1 overflow-hidden flex flex-col" data-id="element-3060">
            <CardHeader className="p-4 border-b border-border/50 bg-card/50 shrink-0" data-id="element-3061">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3062">
                <ShieldAlert className="w-4 h-4 text-primary" data-id="element-3063" /> Regional
                Intelligence
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3064">
              <ul className="space-y-3" data-id="element-3065">
                {regionalReports.filter(isOfficialProduct).slice(0, 3).map(r => <li key={r.id} className="text-xs border-l-2 border-primary/50 pl-3 py-1 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-3066">
                      <div className="font-bold text-foreground/90 mb-1 line-clamp-1" data-id="element-3067">
                        {r.title}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-3068">
                        {r.category} • {r.time.split(' ')[0]}
                      </div>
                    </li>)}
                {regionalReports.filter(isOfficialProduct).length === 0 && <li className="text-xs text-muted-foreground italic" data-id="element-3069">
                    Belum ada produk intelijen resmi di wilayah ini.
                  </li>}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3070">
        {/* Regional KPI Widget (Kabupaten/Kota) */}
        <Card className="glass-panel border-border/50 flex flex-col h-[350px]" data-id="element-3071">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 shrink-0" data-id="element-3072">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3073">
              <Activity className="w-4 h-4 text-primary" data-id="element-3074" /> KPI Kabupaten/Kota
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3075">
            <div className="divide-y divide-border/30" data-id="element-3076">
              {regions.map(r => <div key={r.id} className="p-3 hover:bg-white/5 cursor-pointer transition-colors flex justify-between items-center" onClick={() => navigate(`/kpi/binda/${activeKode}`)} data-id="element-3077">
                  <div data-id="element-3078">
                    <div className="text-xs font-bold" data-id="element-3079">{r.nama}</div>
                    <div className="text-[10px] font-mono text-muted-foreground" data-id="element-3080">
                      {r.laporan} Laporan
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3081">
                    <span className="text-xs font-mono" style={{
                  color: scoreColor(r.score)
                }} data-id="element-3082">
                      {r.score}
                    </span>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0" style={{
                  borderColor: scoreColor(r.score),
                  color: scoreColor(r.score)
                }} data-id="element-3083">
                      {r.grade}
                    </Badge>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Priority Reports Widget */}
        <Card className="glass-panel border-destructive/30 flex flex-col h-[350px]" data-id="element-3084">
          <CardHeader className="p-4 border-b border-destructive/30 bg-destructive/10 shrink-0" data-id="element-3085">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 text-destructive" data-id="element-3086">
              <AlertTriangle className="w-4 h-4" data-id="element-3087" /> Laporan Prioritas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3088">
            <div className="divide-y divide-border/30" data-id="element-3089">
              {priorityReports.length === 0 ? <div className="p-6 text-center text-xs font-mono text-muted-foreground" data-id="element-3090">
                  Tidak ada laporan prioritas.
                </div> : priorityReports.map(r => <div key={r.id} className="p-3 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/reports/${r.id}`)} data-id="element-3091">
                    <div className="flex justify-between items-start mb-1" data-id="element-3092">
                      <UrgencyBadge urgency={r.urgency} className="text-[9px]" data-id="element-3093" />
                      <span className="text-[9px] font-mono text-muted-foreground" data-id="element-3094">
                        {r.time.split(' ')[1]}
                      </span>
                    </div>
                    <h4 className="font-bold text-xs mb-1 line-clamp-1" data-id="element-3095">
                      {r.title}
                    </h4>
                    <p className="text-[10px] text-muted-foreground font-mono" data-id="element-3096">
                      {r.category}
                    </p>
                  </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Regional Alerts Widget */}
        <Card className="glass-panel border-border/50 flex flex-col h-[350px]" data-id="element-3097">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 shrink-0" data-id="element-3098">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3099">
              <ShieldAlert className="w-4 h-4 text-primary" data-id="element-3100" /> Peringatan Dini
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3101">
            <div className="divide-y divide-border/30" data-id="element-3102">
              {regionalAlerts.length === 0 ? <div className="p-6 text-center text-xs font-mono text-muted-foreground" data-id="element-3103">
                  Tidak ada peringatan aktif.
                </div> : regionalAlerts.map(a => <div key={a.id} className="p-3 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/alerts/${a.id}`)} data-id="element-3104">
                    <div className="flex justify-between items-start mb-1" data-id="element-3105">
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${a.type === 'KRITIS' ? 'border-destructive text-destructive' : 'border-[var(--status-standby)] text-[var(--status-standby)]'}`} data-id="element-3106">
                        {a.type}
                      </Badge>
                      <span className="text-[9px] font-mono text-muted-foreground" data-id="element-3107">
                        {a.time}
                      </span>
                    </div>
                    <p className="text-xs text-foreground/90 leading-snug mb-1 line-clamp-2" data-id="element-3108">
                      {a.desc}
                    </p>
                    <p className="text-[9px] text-muted-foreground font-mono" data-id="element-3109">
                      {a.source}
                    </p>
                  </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Personnel List */}
      <Card className="glass-panel border-border/50 flex flex-col" data-id="element-3110">
        <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-3111">
          <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3112">
            <Users className="w-4 h-4 text-primary" data-id="element-3113" /> Status Operatif Personel
          </CardTitle>
          <div className="flex gap-2" data-id="element-3114">
            <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-3115">
              <Filter className="w-4 h-4 mr-2" data-id="element-3116" />
              Filter
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 flex-1 overflow-auto" data-id="element-3117">
          {showFilters && <div className="p-4 border-b border-border/50 bg-black/20" data-id="element-3118">
              <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredPersonnel.length} exportData={filteredPersonnel} exportColumns={exportColumns} exportFilename={`Personel_${regionData.kode}`} data-id="element-3119" />
            </div>}
          {filteredPersonnel.length === 0 ? <EmptyState icon={Search} title="TIDAK ADA PERSONEL" description="Tidak ada personel yang sesuai dengan kriteria filter saat ini." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none rounded-b-lg py-12" data-id="element-3120" /> : <Table data-id="element-3121">
              <TableHeader className="bg-black/20" data-id="element-3122">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-3123">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3124">
                    ID
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3125">
                    Nama
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3126">
                    Pangkat
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3127">
                    Unit
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3128">
                    Status
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3129">
                    Skor KPI
                  </TableHead>
                  <TableHead className="text-right text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3130">
                    Laporan Valid
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-3131">
                {filteredPersonnel.map(person => {
              const unitName = units.find(u => u.id === person.unitId)?.nama || person.unitId;
              return <TableRow key={person.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/kpi/personnel/${person.id}`)} data-id="element-3132">
                      <TableCell className="font-mono text-xs text-primary" data-id="element-3133">
                        {person.id}
                      </TableCell>
                      <TableCell className="font-medium text-sm" data-id="element-3134">
                        {person.nama}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground" data-id="element-3135">
                        {person.pangkat}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground" data-id="element-3136">
                        {unitName}
                      </TableCell>
                      <TableCell data-id="element-3137">
                        <Badge variant="outline" className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0 ${person.status === 'Aktif' ? 'border-[var(--status-active)] text-[var(--status-active)] bg-[var(--status-active)]/10' : person.status === 'Perlu Pembinaan' ? 'border-[var(--destructive)] text-[var(--destructive)] bg-[var(--destructive)]/10' : 'border-muted-foreground text-muted-foreground bg-muted/10'}`} data-id="element-3138">
                          {person.status}
                        </Badge>
                      </TableCell>
                      <TableCell data-id="element-3139">
                        <div className="flex items-center gap-2" data-id="element-3140">
                          <span className="text-xs font-mono" style={{
                      color: scoreColor(person.score)
                    }} data-id="element-3141">
                            {person.score}
                          </span>
                          <Badge variant="outline" className="text-[9px] px-1 py-0" style={{
                      borderColor: scoreColor(person.score),
                      color: scoreColor(person.score)
                    }} data-id="element-3142">
                            {person.grade}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono text-muted-foreground" data-id="element-3143">
                        {person.laporanValid}
                      </TableCell>
                    </TableRow>;
            })}
              </TableBody>
            </Table>}
        </CardContent>
      </Card>
    </div>;
}